import { FC, useMemo } from "react";
import { useWatch } from "react-hook-form";
import { useIntl } from "react-intl";

import { useApiCall } from "@framework/react-hooks";

import { DataExportButton } from "../../../../../../../../components/common/buttons/export-button";
import { downloadCsv, downloadCsvWithPreamble, ICsvPreambleRow } from "../../../../../../../../utils/download-csv";
import { formatMeasurementLabel } from "../../../../../../../../utils/measurement-label";
import { useFormatDateHumanReadable } from "../../../../../../../../hooks";
import {
  idMeasureName,
  idMeasuresOptionsName,
  idObjectName,
  idObjectsOptionsName,
  idParameterName,
  idParametersOptionsName,
  measureTypeName,
} from "../../constants";
import {
  IChartData,
  IChartDataSignal,
  IChartDataOrbit,
  ANALYTICS_VIEW,
  ANALYTICS_MEASURE_TYPE,
} from "../../interfaces";
import { ITableData } from "../table/interfaces";
import { TChartExportFormat } from "../chart-export";
import { hasAnalyticsChartData, hasChartSeriesData, hasTrendTableExportData } from "../../utils/dataAvailability";

interface IAnalyticsOption {
  id: number | string;
  title: string;
  idMeasure?: number;
  measureDate?: string;
}

interface IMeasureExportHeader {
  idTrain?: number;
  TrainName?: string;
  TrainType?: string;
  TrainDescription?: string;
  TrainPosition?: string;
  TrainSerial?: string | null;
  TrainRfidCode?: string | null;
  TrainInstanceNumber?: string;
  EquipmentGroup?: string;
  PointName?: string;
  PointDescription?: string;
  PointDirection?: number | null;
  PointControlledParameter?: number | null;
  PointUnitsName?: string | null;
  MeasureName?: string;
  MeasureDescription?: string;
  MeasureType?: number;
  MeasureRangeType?: number;
  MeasureUnits?: number;
  MeasureParam1?: number | null;
  MeasureParam2?: number | null;
  MeasureResolution?: number | null;
  MeasureAlarmType?: number | null;
  MeasureAveragesCount?: number | null;
  MeasureAveragesType?: number | null;
  MeasureAlarmWarning?: number | null;
  MeasureAlarmCritical?: number | null;
  LastDataDate?: string | null;
  LastValue1?: number | null;
  LastValue2?: number | null;
  RotationFrequencyRpm?: number | null;
  PlannedVibrationSurveyValue?: number | null;
  UnplannedVibrationSurveyValue?: number | null;
  PlannedVibrationSurveyText?: string | null;
  UnplannedVibrationSurveyText?: string | null;
  TaskSpecs?: string | null;
  TrainAvailableTasksText?: string | null;
  TrainParametersPreamble?: string | null;
  StaticParametersPreamble?: string | null;
  KeyphasorsPreamble?: string | null;
  Diagnostician?: string | null;
  MeasurementDevice?: string | null;
}

export interface IAnalyticsExportProps {
  chartData?: IChartData[] | null;
  tableData?: ITableData | null;
  chartDataSignal?: IChartDataSignal[] | null;
  chartDataOrbit?: IChartDataOrbit[] | null;
  fileName: string;
  view: ANALYTICS_VIEW;
  onDiagramExport?: (format: TChartExportFormat) => void;
}

const signalY = (item: IChartDataSignal["items"][number]): number =>
  typeof item.y === "number" ? item.y : item.y?.real ?? 0;

function formatMeasureTableValue(value: number | null | undefined): string | number {
  if (value === null || value === undefined) {
    return "";
  }
  if (!Number.isFinite(value)) {
    return String(value);
  }
  const rounded = Math.round(value * 100) / 100;
  if (Object.is(rounded, -0)) {
    return 0;
  }
  if (rounded === Math.trunc(rounded)) {
    return rounded;
  }
  return rounded.toFixed(2);
}

const normalizeToArray = <T,>(value: T | T[] | null | undefined): T[] =>
  value == null ? [] : Array.isArray(value) ? value : [value];

const joinValues = (values: Array<string | number | null | undefined>): string =>
  values
    .map(value => (value == null ? "" : String(value).trim()))
    .filter(Boolean)
    .join(" | ");

const formatRangeValue = (value: number | null | undefined): string => {
  const formatted = formatMeasureTableValue(value);
  return formatted === "" ? "" : String(formatted);
};

const formatRotationFrequency = (value: number | null | undefined, t: (ru: string, en: string) => string): string => {
  if (value === null || value === undefined || !Number.isFinite(value)) {
    return "";
  }
  const rpm = Number(formatRangeValue(value));
  const hz = Number(formatRangeValue(value / 60));
  return `${rpm} ${t("об/мин", "rpm")} (${hz} ${t("Гц", "Hz")})`;
};

const splitPreambleLines = (text: string | null | undefined): ICsvPreambleRow[] =>
  (text?.split(/\r?\n/).filter(line => line.trim() !== "") ?? []).map(line => {
    const sep = ": ";
    const i = line.indexOf(sep);
    if (i === -1) {
      return { label: "", value: line.trim() };
    }
    return { label: line.slice(0, i).trim(), value: line.slice(i + sep.length).trim() };
  });

const formatAveragesType = (averagesType: number | null | undefined, t: (ru: string, en: string) => string): string => {
  if (averagesType == null) {
    return "";
  }
  switch (averagesType) {
    case 0:
      return t("Без усреднений", "No averaging");
    case 1:
      return t("Линейные", "Linear");
    case 2:
      return t("Пиковые", "Peak");
    case 3:
      return t("Экспоненциальные", "Exponential");
    default:
      return String(averagesType);
  }
};

const splitCsvList = (value: string | null | undefined): string[] =>
  value
    ?.split(",")
    .map(item => item.trim())
    .filter(Boolean) ?? [];

const vibrationSurveyCsvValue = (
  fullText: string | null | undefined,
  numericDays: number | null | undefined,
): string => {
  const trimmed = fullText?.trim();
  if (trimmed) {
    const sep = ": ";
    const i = trimmed.indexOf(sep);
    const tail = i >= 0 ? trimmed.slice(i + sep.length).trim() : trimmed;
    if (tail) {
      return tail;
    }
  }
  if (numericDays != null) {
    return String(formatMeasureTableValue(numericDays));
  }
  return "";
};

export const AnalyticsExport: FC<IAnalyticsExportProps> = props => {
  const { chartData, tableData, chartDataSignal, chartDataOrbit, fileName, view, onDiagramExport } = props;

  const { formatMessage, locale } = useIntl();
  const formatDate = useFormatDateHumanReadable();
  const measureType = useWatch({ name: measureTypeName }) as ANALYTICS_MEASURE_TYPE | undefined;
  const idObject = useWatch({ name: idObjectName }) as number | string | null | undefined;
  const idParameter = useWatch({ name: idParameterName }) as number | string | null | undefined;
  const idMeasure = useWatch({ name: idMeasureName }) as number | number[] | null | undefined;
  const objectOptions = (useWatch({ name: idObjectsOptionsName }) as IAnalyticsOption[] | undefined) ?? [];
  const parameterOptions = (useWatch({ name: idParametersOptionsName }) as IAnalyticsOption[] | undefined) ?? [];
  const measureOptions = (useWatch({ name: idMeasuresOptionsName }) as IAnalyticsOption[] | undefined) ?? [];

  const headersId = "pages.scheme.analytics.headers";
  const isRussian = locale.toLowerCase().startsWith("ru");
  const t = (ru: string, en: string) => (isRussian ? ru : en);
  const hasSignalChartData = hasChartSeriesData(chartDataSignal);
  const hasChartExportData = hasAnalyticsChartData({ chartData, chartDataSignal, chartDataOrbit });
  const hasTableExportData = hasTrendTableExportData(chartData, tableData);

  const showSpectrumCsv = useMemo(
    () => measureType === ANALYTICS_MEASURE_TYPE.SPECTRUM && hasSignalChartData,
    [measureType, hasSignalChartData],
  );

  const selectedObject = useMemo(
    () => objectOptions.find(option => String(option.id) === String(idObject)) ?? null,
    [idObject, objectOptions],
  );

  const selectedParameter = useMemo(
    () => parameterOptions.find(option => String(option.id) === String(idParameter)) ?? null,
    [idParameter, parameterOptions],
  );

  const selectedMeasureOptions = useMemo(() => {
    const selectedIds = new Set(normalizeToArray(idMeasure).map(value => String(value)));
    return measureOptions.filter(option => selectedIds.has(String(option.id)));
  }, [idMeasure, measureOptions]);

  const { fn: getMeasureExportHeader } = useApiCall(
    (api, { stableMeasureId }: { stableMeasureId: number }) => {
      return api.fetchJson({
        url: `/journal/measure/${stableMeasureId}/export-header`,
        method: "GET",
      }) as Promise<IMeasureExportHeader>;
    },
    { success: false, error: false },
  );

  const handleTableExport = (): void => {
    if (hasTableExportData && chartData && tableData) {
      downloadCsv(
        chartData.reduce((acc: any[], data) => {
          acc.push(
            ...data.items.map(item => {
              const result: any = {
                [tableData.type]: data.name,
                date: formatDate(item.date),
                value: formatMeasureTableValue(item.value),
              };

              if (data.units) {
                result.units = data.units;
              }

              // eslint-disable-next-line @typescript-eslint/no-unsafe-return
              return result;
            }),
          );
          // eslint-disable-next-line @typescript-eslint/no-unsafe-return
          return acc;
        }, []),
        fileName,
        [
          { field: tableData.type, title: formatMessage({ id: `${headersId}.${tableData.type}` }) },
          { field: "date", title: formatMessage({ id: `${headersId}.date` }) },
          { field: "value", title: formatMessage({ id: `${headersId}.value` }) },
          { field: "units", title: formatMessage({ id: `${headersId}.units` }) },
        ],
      );
    }
  };

  const handleSpectrumCsvExport = async (): Promise<void> => {
    if (!chartDataSignal?.length) return;

    const firstSelectedMeasureId = selectedMeasureOptions[0]?.idMeasure;
    let exportHeader: IMeasureExportHeader | null = null;
    if (typeof firstSelectedMeasureId === "number") {
      try {
        exportHeader = (await getMeasureExportHeader(undefined, { stableMeasureId: firstSelectedMeasureId })) ?? null;
      } catch {
        exportHeader = null;
      }
    }
    const firstSeries = chartDataSignal[0];
    const xValues = firstSeries?.items.map(item => item.x) ?? [];
    const minX = xValues.length ? Math.min(...xValues) : null;
    const maxX = xValues.length ? Math.max(...xValues) : null;

    const measureTypeLabel =
      exportHeader?.MeasureType != null
        ? `${formatMessage({ id: `enums.measurementType.${exportHeader.MeasureType}` })}${
            exportHeader.MeasureRangeType != null && exportHeader.MeasureRangeType !== 255
              ? ` (${formatMessage({ id: `enums.measurementRangeType.${exportHeader.MeasureRangeType}` })})`
              : ""
          }`
        : measureType === ANALYTICS_MEASURE_TYPE.SPECTRUM
          ? formatMessage({ id: "enums.measureType.SPECTRUM" })
          : measureType === ANALYTICS_MEASURE_TYPE.SIGNAL_FORM
            ? formatMessage({ id: "enums.measureType.SIGNAL_FORM" })
            : measureType === ANALYTICS_MEASURE_TYPE.ORBIT
              ? formatMessage({ id: "enums.measureType.ORBIT" })
              : formatMessage({ id: "enums.measureType.TREND" });

    const value1 = joinValues(
      chartDataSignal.map(series => {
        const formatted = formatMeasureTableValue(series.value1);
        return formatted === "" ? "" : `${formatted} ${series.unitsY || ""}`.trim();
      }),
    );
    const value2 = joinValues(chartDataSignal.map(series => formatMeasureTableValue(series.value2)).map(String));
    const rotationFrequency = joinValues(
      chartDataSignal.map(series => formatRotationFrequency(series.rotationFrequencyRpm, t)).filter(Boolean),
    );
    const fallbackValue1 =
      exportHeader?.LastValue1 != null
        ? `${formatMeasureTableValue(exportHeader.LastValue1)} ${
            exportHeader.MeasureUnits != null ? formatMessage({ id: `enums.units.${exportHeader.MeasureUnits}` }) : ""
          }`.trim()
        : "";
    const fallbackValue2 =
      exportHeader?.LastValue2 != null ? String(formatMeasureTableValue(exportHeader.LastValue2)) : "";
    const fallbackRotationFrequency = formatRotationFrequency(exportHeader?.RotationFrequencyRpm, t);
    const plannedVibrationSurvey = vibrationSurveyCsvValue(
      exportHeader?.PlannedVibrationSurveyText,
      exportHeader?.PlannedVibrationSurveyValue ?? null,
    );
    const unplannedVibrationSurvey = vibrationSurveyCsvValue(
      exportHeader?.UnplannedVibrationSurveyText,
      exportHeader?.UnplannedVibrationSurveyValue ?? null,
    );
    const signalizationType =
      firstSeries?.alarmType != null
        ? String(firstSeries.alarmType)
        : exportHeader?.MeasureAlarmType != null
          ? String(exportHeader.MeasureAlarmType)
          : t("Не определен", "Not defined");
    const tasksFromSchedule = exportHeader?.TrainAvailableTasksText?.trim() ?? "";
    const tasksValue =
      tasksFromSchedule ||
      joinValues(
        splitCsvList(exportHeader?.TaskSpecs).map(taskSpec =>
          formatMessage({ id: `enums.taskSpec.${taskSpec}`, defaultMessage: taskSpec }),
        ),
      );
    const pointDirectionLabel =
      exportHeader?.PointDirection != null
        ? formatMessage({ id: `enums.pointDirection.${exportHeader.PointDirection}` })
        : "";
    const pointTitle = exportHeader?.PointName ?? firstSeries?.pointName ?? selectedObject?.title ?? "";
    const pointLine = pointDirectionLabel !== "" ? `${pointTitle} (${pointDirectionLabel})`.trim() : pointTitle;
    const controlledParamLabel =
      exportHeader?.PointControlledParameter != null
        ? formatMessage({
            id: `pages.journals.popup.train.points.${exportHeader.PointControlledParameter}`,
            defaultMessage: String(exportHeader.PointControlledParameter),
          })
        : "";
    const unitsSuffix =
      exportHeader?.PointUnitsName != null && exportHeader.PointUnitsName.trim() !== ""
        ? `(${exportHeader.PointUnitsName})`
        : "()";
    const parameterLine =
      controlledParamLabel !== ""
        ? `${controlledParamLabel} ${unitsSuffix}`.trim()
        : exportHeader?.MeasureDescription ?? firstSeries?.measurementDescription ?? selectedParameter?.title ?? "";
    const averagingValue =
      exportHeader?.MeasureAveragesCount != null || exportHeader?.MeasureAveragesType != null
        ? `${
            exportHeader?.MeasureAveragesCount != null ? formatMeasureTableValue(exportHeader.MeasureAveragesCount) : ""
          }${
            exportHeader?.MeasureAveragesType != null
              ? ` (${formatAveragesType(exportHeader.MeasureAveragesType, t)})`
              : ""
          }`.trim()
        : "";
    const allSpectrumY = chartDataSignal.flatMap(series => series.items.map(signalY));
    const spreadValue =
      allSpectrumY.length > 0 ? formatMeasureTableValue(Math.max(...allSpectrumY) - Math.min(...allSpectrumY)) : "";

    const preambleRows: ICsvPreambleRow[] = [
      { label: t("Агрегат", "Machine"), value: exportHeader?.TrainName ?? "" },
      { label: t("Тип", "Type"), value: exportHeader?.TrainType ?? "" },
      { label: t("Описание", "Description"), value: exportHeader?.TrainDescription ?? "" },
      { label: t("Позиция", "Position"), value: exportHeader?.TrainPosition ?? "" },
      ...(exportHeader?.TrainSerial != null && String(exportHeader.TrainSerial).trim() !== ""
        ? [{ label: t("Инвентарный номер", "Inventory number"), value: String(exportHeader.TrainSerial) }]
        : []),
      { label: t("Номер актива", "Asset number"), value: exportHeader?.TrainInstanceNumber ?? "" },
      ...(exportHeader?.TrainRfidCode != null && String(exportHeader.TrainRfidCode).trim() !== ""
        ? [{ label: t("Цифровой индекс", "Digital index"), value: String(exportHeader.TrainRfidCode) }]
        : []),
      { label: t("Ранг критичности", "Criticality rank"), value: exportHeader?.EquipmentGroup ?? "" },
      ...splitPreambleLines(exportHeader?.TrainParametersPreamble ?? null),
      ...splitPreambleLines(exportHeader?.StaticParametersPreamble ?? null),
      ...splitPreambleLines(exportHeader?.KeyphasorsPreamble ?? null),
      { label: t("Задания", "Tasks"), value: tasksValue },
      {
        label: t("Плановое виброобследование", "Scheduled vibration inspection"),
        value: plannedVibrationSurvey,
      },
      {
        label: t("Внеплановое виброобследование", "Unscheduled vibration inspection"),
        value: unplannedVibrationSurvey,
      },
      {
        label: t("Точка", "Point"),
        value: pointLine,
      },
      {
        label: t("Описание", "Description"),
        value: exportHeader?.PointDescription ?? firstSeries?.pointDescription ?? "",
      },
      {
        label: t("Параметр", "Parameter"),
        value: parameterLine,
      },
      {
        label: t("Замер", "Measurement"),
        value:
          formatMeasurementLabel(
            firstSeries?.measurementName ?? exportHeader?.MeasureName,
            firstSeries?.measurementDescription ?? exportHeader?.MeasureDescription,
          ) || joinValues(selectedMeasureOptions.map(option => option.title)),
      },
      { label: t("Тип", "Type"), value: measureTypeLabel },
      {
        label: t("Единицы", "Units"),
        value:
          exportHeader?.MeasureUnits != null
            ? formatMessage({ id: `enums.units.${exportHeader.MeasureUnits}` })
            : firstSeries?.unitsY ?? "",
      },
      {
        label: t("Частотный диапазон", "Frequency range"),
        value:
          minX !== null && maxX !== null
            ? `${formatRangeValue(minX)} - ${formatRangeValue(maxX)} ${firstSeries?.unitsX ?? ""}`.trim()
            : "",
      },
      {
        label: t("Разрешение", "Resolution"),
        value:
          firstSeries?.resolution != null
            ? `${formatRangeValue(firstSeries.resolution)} ${t("линий", "lines")}`
            : exportHeader?.MeasureResolution != null
              ? `${formatRangeValue(exportHeader.MeasureResolution)} ${t("линий", "lines")}`
              : firstSeries?.items.length
                ? `${firstSeries.items.length} ${t("линий", "lines")}`
                : "",
      },
      { label: t("Усреднение", "Averaging"), value: averagingValue },
      { label: t("Тип сигнализации", "Signalization type"), value: signalizationType },
      {
        label: t("Данные", "Data"),
        value:
          joinValues(chartDataSignal.map(series => (series.date ? formatDate(series.date) : "")).filter(Boolean)) ||
          (exportHeader?.LastDataDate ? formatDate(exportHeader.LastDataDate) : ""),
      },
      { label: t("Значение 1", "Value 1"), value: value1 || fallbackValue1 },
      { label: t("Значение 2", "Value 2"), value: value2 || fallbackValue2 },
      { label: t("Разброс", "Spread"), value: spreadValue === "" ? "" : String(spreadValue) },
      { label: t("Частота вращения", "Rotation frequency"), value: rotationFrequency || fallbackRotationFrequency },
      { label: t("Средство измерения", "Measurement device"), value: exportHeader?.MeasurementDevice ?? "" },
      { label: t("Диагност", "Diagnostician"), value: exportHeader?.Diagnostician ?? "" },
    ];

    const rows = chartDataSignal.flatMap(data =>
      data.items.map(item => {
        const y = signalY(item);
        return {
          seriesName: data.name,
          x: item.x,
          y: y % 1 !== 0 ? Number(y.toFixed(6)) : y,
          unitsX: data.unitsX,
          unitsY: data.unitsY,
        };
      }),
    );

    downloadCsvWithPreamble(preambleRows, rows, fileName, [
      { field: "seriesName", title: formatMessage({ id: `${headersId}.spectrumSeries` }) },
      { field: "x", title: formatMessage({ id: `${headersId}.spectrumX` }) },
      { field: "y", title: formatMessage({ id: `${headersId}.spectrumY` }) },
      { field: "unitsX", title: formatMessage({ id: `${headersId}.spectrumUnitsX` }) },
      { field: "unitsY", title: formatMessage({ id: `${headersId}.spectrumUnitsY` }) },
    ]);
  };

  const handleDiagramExport = (format?: "png" | "jpg" | "csv"): void => {
    if (format === "csv") {
      void handleSpectrumCsvExport();
      return;
    }
    if (format === "png" || format === "jpg") {
      onDiagramExport?.(format);
    }
  };

  if (view === ANALYTICS_VIEW.CHART && hasChartExportData) {
    return <DataExportButton handleExport={handleDiagramExport} type="image" showCsvInImageMenu={showSpectrumCsv} />;
  } else if (view === ANALYTICS_VIEW.TABLE && hasTableExportData) {
    return <DataExportButton handleExport={handleTableExport} type="csv" />;
  } else {
    return null;
  }
};
